import React, { useMemo, useEffect, useRef, useState } from 'react'
import Popup from 'components/popup/Popup'
import { ReportImageUpscale } from 'pages/ReportImagePopup/ReportImageUpscale/ReportImageUpscale'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { IStandardAiDatasetDto } from 'core/api/dto/StandardAiDatasetDto'
import { useUpdateStandardAiDatasetMutation } from 'core/api/StandardAiDataset'
import { AppMultiDropdown } from 'components/AppDropdown/AppMultiDropdown'

type StatusType = 'match' | 'no-match' | 'processing' | 'not-labeled'

interface Props {
  active: boolean
  onClose: () => void
  reportImagePopupData: IStandardAiDatasetDto[]
  photo?: string | null
  dimensionPhoto?: string | null
  name?: string
  isCategory?: boolean
  temperature?: number | string | null
  standardTempMin?: number | null
  standardTempMax?: number | null
  mismatchOptions: { id: number; name: string }[]
  hasMore?: boolean
  isFetching?: boolean
  onLoadMore?: () => void
}

export const DishAiImagePopup: React.FC<Props> = ({
  active,
  onClose,
  reportImagePopupData,
  photo,
  dimensionPhoto,
  name,
  isCategory = true,
  temperature,
  standardTempMin,
  standardTempMax,
  mismatchOptions,
  hasMore = false,
  isFetching = false,
  onLoadMore,
}) => {
  const [status, setStatus] = useState<StatusType>('not-labeled')
  const [selectedMismatch, setSelectedMismatch] = useState<
    { id: number; name: string }[] | null
  >(null)
  const [savingId, setSavingId] = useState<number | null>(null)

  const statusData = [
    { key: 'not-labeled', label: 'Не размечено' },
    { key: 'match', label: 'Соответствует' },
    { key: 'no-match', label: 'Не соответствует' },
  ]

  const prevStatusRef = useRef<StatusType | null>(null)
  const prevMismatchRef = useRef<{ id: number; name: string }[] | null>(null)
  const currentItemIdRef = useRef<number | null>(null)
  const lastIndexRef = useRef(0)
  const pendingNextRef = useRef(false)

  const [searchParams, setSearchParams] = useSearchParams()
  const { t } = useTranslation()

  const id = useMemo(() => searchParams.get('id'), [searchParams])

  const list = useMemo<IStandardAiDatasetDto[]>(() => {
    return Array.isArray(reportImagePopupData) ? reportImagePopupData : []
  }, [reportImagePopupData])

  const rawIndex = useMemo(() => {
    if (!id) return -1
    return list.findIndex((i) => String(i?.id ?? '') === String(id))
  }, [id, list])

  // запись пропала из списка (например, размеченное фото ушло из-под фильтра) —
  // показываем ту, что встала на её место, а если это был конец списка — последнюю
  const itemIndex = useMemo(() => {
    if (rawIndex !== -1 || list.length === 0) return rawIndex
    return Math.min(lastIndexRef.current, list.length - 1)
  }, [rawIndex, list.length])

  const item = useMemo<IStandardAiDatasetDto | undefined>(() => {
    if (itemIndex === -1) return undefined
    return list[itemIndex]
  }, [list, itemIndex])

  const prevItem = useMemo(() => {
    if (itemIndex <= 0) return undefined
    return list[itemIndex - 1]
  }, [itemIndex, list])

  const nextItem = useMemo(() => {
    if (itemIndex === -1 || itemIndex >= list.length - 1) return undefined
    return list[itemIndex + 1]
  }, [itemIndex, list])

  useEffect(() => {
    if (itemIndex !== -1) lastIndexRef.current = itemIndex
  }, [itemIndex])

  const onButtonPreviousClick = () => {
    if (!prevItem) return
    const newParams = new URLSearchParams(searchParams)
    newParams.set('id', String(prevItem.id))
    setSearchParams(newParams, { replace: true })
  }

  const onButtonNextClick = () => {
    if (!nextItem) {
      if (hasMore && !isFetching && onLoadMore) {
        pendingNextRef.current = true
        onLoadMore()
      }
      return
    }
    const newParams = new URLSearchParams(searchParams)
    newParams.set('id', String(nextItem.id))
    setSearchParams(newParams, { replace: true })
  }

  // после дозагрузки по «Следующей» переходим на первую новую запись
  useEffect(() => {
    if (!pendingNextRef.current || isFetching) return
    pendingNextRef.current = false
    if (nextItem) {
      const newParams = new URLSearchParams(searchParams)
      newParams.set('id', String(nextItem.id))
      setSearchParams(newParams, { replace: true })
    }
  }, [nextItem, isFetching, searchParams, setSearchParams])

  // id в URL указывает на пропавшую запись — подменяем на показанную
  useEffect(() => {
    if (rawIndex !== -1 || !item || !id) return
    const newParams = new URLSearchParams(searchParams)
    newParams.set('id', String(item.id))
    setSearchParams(newParams, { replace: true })
  }, [rawIndex, item, id, searchParams, setSearchParams])

  const currentPhoto = useMemo(() => {
    return item?.dish.imageOnS3 ?? item?.dish.imagePreview ?? photo ?? null
  }, [item, photo])

  const currentDimensionPhoto = useMemo(() => {
    return item?.imageOnS3 ?? item?.imagePreviewOnS3 ?? currentPhoto ?? null
  }, [item, currentPhoto])

  const currentName = useMemo(() => {
    return item?.dish?.name ?? item?.name ?? name ?? item?.imageOnS3 ?? ''
  }, [item, name])

  const [updateStandardAiDataset] = useUpdateStandardAiDatasetMutation()

  const statusToApi = (s: StatusType) => {
    switch (s) {
      case 'match':
        return 'Compliant'
      case 'no-match':
        return 'Non_compliant'
      case 'processing':
        return 'Processing'
      case 'not-labeled':
      default:
        return 'Not_determined'
    }
  }

  const buildSnakePayload = (
    it: IStandardAiDatasetDto,
    apiStatusValue?: string,
    selectedMismatchParam?: { id: number; name: string }[] | null,
  ) => {
    const finalStatus =
      apiStatusValue ?? it.complianceWithStandards ?? 'Not_determined'

    const nonCompliantTypeIds =
      finalStatus === 'Non_compliant' && selectedMismatchParam?.length
        ? selectedMismatchParam.map((item) => item.id)
        : null

    return {
      image: it.imagePreviewOnS3 ?? null,
      imageOnS3: it.imageOnS3 ?? null,
      complianceWithStandards: finalStatus,
      nonCompliantTypeIds: nonCompliantTypeIds,
    }
  }
  const handleSave = async (
    it: IStandardAiDatasetDto | undefined,
    newStatus: StatusType | null,
    newSelectedMismatch?: { id: number; name: string }[] | null,
  ) => {
    if (!it) return
    const apiStatus = newStatus
      ? statusToApi(newStatus)
      : it.complianceWithStandards
    const payload = buildSnakePayload(
      it,
      apiStatus,
      newSelectedMismatch ?? selectedMismatch,
    )

    setSavingId(it.id)
    try {
      await updateStandardAiDataset({ id: it.id, data: payload }).unwrap()
    } catch (err) {
    } finally {
      setSavingId(null)
    }
  }

  const displayOptions = useMemo(() => {
    const map = new Map()
    ;[...mismatchOptions, ...(selectedMismatch ?? [])].forEach((opt) => {
      map.set((opt.name ?? '').toLowerCase().trim(), opt)
    })
    return Array.from(map.values())
  }, [mismatchOptions, selectedMismatch])

  useEffect(() => {
    if (!item) {
      setStatus('not-labeled')
      setSelectedMismatch(null)
      prevStatusRef.current = null
      prevMismatchRef.current = null
      currentItemIdRef.current = null

      const currentMismatchType = searchParams.get('mismatchType')
      if (currentMismatchType) {
        const newParams = new URLSearchParams(searchParams)
        newParams.delete('mismatchType')
        setSearchParams(newParams, { replace: true })
      }
      return
    }

    if (currentItemIdRef.current === item.id) {
      return
    }

    const statusMap: Record<string, StatusType> = {
      Compliant: 'match',
      Non_compliant: 'no-match',
      Not_determined: 'not-labeled',
    }

    const newStatus = statusMap[item.complianceWithStandards]
    let newMismatch: { id: number; name: string }[] | null = null

    if (item.nonCompliantTypes && item.nonCompliantTypes.length > 0) {
      const matchedOptions = mismatchOptions.filter((option) => {
        const isMatch = item.nonCompliantTypes!.some((mismatchType) => {
          const match =
            option.name.toLowerCase().trim() ===
            mismatchType.toLowerCase().trim()
          if (match) {
            console.log(
              `✅ Found match: "${option.name}" === "${mismatchType}"`,
            )
          }
          return match
        })
        return isMatch
      })

      newMismatch = matchedOptions.length > 0 ? matchedOptions : null
    }

    setStatus(newStatus)
    setSelectedMismatch(newMismatch)

    prevStatusRef.current = newStatus
    prevMismatchRef.current = newMismatch
    currentItemIdRef.current = item.id

    const mismatchTypeParam = newMismatch?.[0]?.id
    const currentMismatchType = searchParams.get('mismatchType')

    if (
      mismatchTypeParam !== undefined &&
      String(mismatchTypeParam) !== currentMismatchType
    ) {
      const newParams = new URLSearchParams(searchParams)
      newParams.set('mismatchType', String(mismatchTypeParam))
      setSearchParams(newParams, { replace: true })
    } else if (!mismatchTypeParam && currentMismatchType) {
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('mismatchType')
      setSearchParams(newParams, { replace: true })
    }
  }, [item, mismatchOptions, searchParams, setSearchParams])

  const onStatusChange = (newStatus: StatusType) => {
    if (prevStatusRef.current === newStatus) return

    setStatus(newStatus)
    prevStatusRef.current = newStatus
    if (!item) return
    if (newStatus === 'no-match') return

    setSelectedMismatch(null)
    prevMismatchRef.current = null
    handleSave(item, newStatus, selectedMismatch)
  }

  const onMismatchChange = (values: { id: number; name: string }[] | null) => {
    setSelectedMismatch(values)
    prevMismatchRef.current = values

    if (!item) return

    if (values && values.length > 0) {
      setStatus('no-match')
      prevStatusRef.current = 'no-match'
      handleSave(item, 'no-match', values)
    } else {
      setStatus('not-labeled')
      prevStatusRef.current = 'not-labeled'
      handleSave(item, 'not-labeled', null)
    }
  }

  if (!active || list.length === 0) return null

  return (
    <Popup closePopup={onClose}>
      <div className="dish-ai-popup__container">
        <div className="dish-ai-popup__header">
          <span>{currentName}</span>
          <button className="dish-ai-popup__image-wrapper" onClick={onClose}>
            <i
              className="an-ico an-ico-close"
              style={{ fontSize: '12px', color: 'white' }}
            />
          </button>
        </div>

        <div className="dish-ai-popup__controls">
          <div className="dish-ai-popup__status">
            <span className="dish-ai-popup__label">Статус</span>
            <div className="dish-ai-popup__radios">
              {statusData.map(({ key, label }) => (
                <label key={key} className="dish-ai-popup__radio-item">
                  <input
                    type="radio"
                    name="reportStatus"
                    checked={status === key}
                    onChange={() => onStatusChange(key as StatusType)}
                    disabled={savingId === item?.id}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>

          {status === 'no-match' && (
            <div className="dish-ai-popup__type">
              <label className="dish-ai-popup__label">Тип несоответствия</label>
              <AppMultiDropdown<
                { id: number; name: string },
                keyof { id: number; name: string }
              >
                data={displayOptions}
                value={selectedMismatch ?? []}
                onChange={(vals) => {
                  const arr = (vals as { id: number; name: string }[]) || []
                  onMismatchChange(arr.length > 0 ? arr : null)
                }}
                propToShowInList="name"
                propToShowInInput="name"
                placeholder="Выбрать"
                border
                fullWidth
                onDeleteFromList={(id) => {
                  const updated = (selectedMismatch ?? []).filter(
                    (item) => item.id !== id,
                  )
                  onMismatchChange(updated.length > 0 ? updated : null)
                }}
              />
            </div>
          )}
        </div>

        <div className="dish-ai-popup__body">
          <div className="dish-ai-popup__photos">
            {currentPhoto && (
              <div className="dish-ai-popup__photo">
                <ReportImageUpscale
                  photo={currentPhoto}
                  standardTempMax={item?.dish?.standardTempMax}
                  standardTempMin={item?.dish?.standardTempMin}
                />
              </div>
            )}
            {currentDimensionPhoto && (
              <div className="dish-ai-popup__photo">
                <ReportImageUpscale
                  photo={currentDimensionPhoto}
                  isDimension
                  isFact
                  isCategory={isCategory}
                  temperature={item?.temperature ?? ''}
                />
              </div>
            )}
          </div>
        </div>

        <div className="dish-ai-popup__footer">
          <button
            className="dish-ai-popup__button dish-ai-popup__button_prev"
            onClick={onButtonPreviousClick}
            disabled={!prevItem}
            title={!prevItem ? 'Нет предыдущего элемента' : undefined}
          >
            {t('Предыдущая')}
          </button>

          <a
            className="dish-ai-popup__button dish-ai-popup__button__redirect btn"
            href={item?.imageOnS3 ?? item?.image ?? '#'}
            target="_blank"
            rel="noopener noreferrer"
          >
            {window.innerWidth < 600 ? (
              <i className="an-ico an-ico-search" />
            ) : (
              <> {t('Перейти')}</>
            )}
          </a>

          <button
            className="dish-ai-popup__button dish-ai-popup__button"
            onClick={onButtonNextClick}
            disabled={(!nextItem && !hasMore) || (!nextItem && isFetching)}
            title={
              !nextItem && !hasMore ? 'Нет следующего элемента' : undefined
            }
          >
            {t('Следующая')}
          </button>
        </div>
      </div>
    </Popup>
  )
}

export default DishAiImagePopup
